import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { mockROIData } from '../mock';
import { TrendingUp, TrendingDown, Zap, PiggyBank, Timer } from 'lucide-react';

const ROICalculator = () => {
  const [selectedSystem, setSelectedSystem] = useState(mockROIData[0]);

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Calculate Your Savings
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See how much you can save with solar energy - real numbers, real savings
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Calculator card with gradient header bar */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 0 30px rgba(44,200,75,0.15)' }}
          >
            {/* Gradient header bar */}
            <div
              style={{
                background: 'linear-gradient(90deg, #2CC84B, #00BCD4, #3F51B5)',
                height: '6px',
              }}
            />

            <div className="bg-white p-6 md:p-8 rounded-b-2xl">
              {/* System Size Selector */}
              <Tabs defaultValue="3kW" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-8">
                  {mockROIData.map((system) => (
                    <TabsTrigger
                      key={system.capacity}
                      value={system.capacity}
                      onClick={() => setSelectedSystem(system)}
                      className="text-base md:text-lg font-semibold"
                    >
                      {system.capacity} System
                    </TabsTrigger>
                  ))}
                </TabsList>

                {mockROIData.map((system) => (
                  <TabsContent key={system.capacity} value={system.capacity}>
                    {/* Before/After Comparison */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      {/* Before Solar */}
                      <Card className="border-red-200 bg-red-50/50">
                        <CardHeader>
                          <CardTitle className="text-red-700 flex items-center gap-2">
                            <Zap className="w-5 h-5" />
                            Before Solar
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-center">
                            <p className="text-sm text-gray-600 mb-2">Average Monthly Bill</p>
                            <p className="text-4xl font-bold text-red-600">{system.avgBillBefore}</p>
                            <p className="text-sm text-gray-500 mt-2">From grid electricity</p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* After Solar */}
                      <Card style={{ borderColor: 'rgba(44,200,75,0.3)', background: 'rgba(44,200,75,0.04)' }}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2" style={{ color: '#2CC84B' }}>
                            <TrendingDown className="w-5 h-5" />
                            After Solar
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-center">
                            <p className="text-sm text-gray-600 mb-2">Average Monthly Bill</p>
                            <p
                              className="text-4xl font-bold"
                              style={{
                                background: 'linear-gradient(90deg, #2CC84B, #00BCD4)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                              }}
                            >
                              {system.avgBillAfter}
                            </p>
                            <p className="text-sm text-gray-500 mt-2">Minimal grid usage</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Savings Breakdown */}
                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                      <Card
                        style={{
                          background: 'linear-gradient(135deg, rgba(44,200,75,0.08), rgba(0,188,212,0.12))',
                          borderColor: 'rgba(44,200,75,0.25)',
                        }}
                      >
                        <CardContent className="pt-6 text-center">
                          <PiggyBank className="w-10 h-10 mx-auto mb-3" style={{ color: '#2CC84B' }} />
                          <p className="text-sm text-gray-600 mb-2">Monthly Savings</p>
                          <p
                            className="text-3xl font-bold"
                            style={{
                              background: 'linear-gradient(90deg, #2CC84B, #00BCD4)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {system.monthlySavings}
                          </p>
                        </CardContent>
                      </Card>

                      <Card
                        style={{
                          background: 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(63,81,181,0.08))',
                          borderColor: 'rgba(0,188,212,0.25)',
                        }}
                      >
                        <CardContent className="pt-6 text-center">
                          <TrendingUp className="w-10 h-10 mx-auto mb-3" style={{ color: '#00BCD4' }} />
                          <p className="text-sm text-gray-600 mb-2">Yearly Savings</p>
                          <p
                            className="text-3xl font-bold"
                            style={{
                              background: 'linear-gradient(90deg, #00BCD4, #3F51B5)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {system.yearlySavings}
                          </p>
                        </CardContent>
                      </Card>

                      <Card
                        style={{
                          background: 'linear-gradient(135deg, rgba(63,81,181,0.08), rgba(44,200,75,0.08))',
                          borderColor: 'rgba(63,81,181,0.2)',
                        }}
                      >
                        <CardContent className="pt-6 text-center">
                          <Timer className="w-10 h-10 mx-auto mb-3" style={{ color: '#3F51B5' }} />
                          <p className="text-sm text-gray-600 mb-2">Payback Period</p>
                          <p
                            className="text-3xl font-bold"
                            style={{
                              background: 'linear-gradient(90deg, #3F51B5, #2CC84B)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {system.roi}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Investment Breakdown */}
                    <Card style={{ border: '2px solid rgba(44,200,75,0.25)' }}>
                      <CardHeader
                        style={{
                          background: 'linear-gradient(90deg, rgba(44,200,75,0.1), rgba(0,188,212,0.08))',
                        }}
                      >
                        <CardTitle className="text-center text-2xl">Investment Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center pb-3 border-b">
                            <span className="text-gray-700 font-medium">System Cost</span>
                            <span className="text-xl font-bold text-gray-900">{system.systemCost}</span>
                          </div>
                          <div className="flex justify-between items-center pb-3 border-b">
                            <span className="font-medium" style={{ color: '#2CC84B' }}>Government Subsidy</span>
                            <span className="text-xl font-bold" style={{ color: '#2CC84B' }}>- {system.subsidy}</span>
                          </div>
                          <div
                            className="flex justify-between items-center pt-2 p-4 rounded-lg"
                            style={{
                              background: 'linear-gradient(90deg, rgba(44,200,75,0.08), rgba(0,188,212,0.08))',
                            }}
                          >
                            <span className="text-lg font-bold text-gray-900">Your Investment</span>
                            <span
                              className="text-3xl font-bold"
                              style={{
                                background: 'linear-gradient(90deg, #2CC84B, #00BCD4)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                              }}
                            >
                              {system.finalCost}
                            </span>
                          </div>
                        </div>

                        {/* 25-Year Savings Highlight */}
                        <div
                          className="mt-6 text-white p-6 rounded-xl text-center"
                          style={{
                            background: 'linear-gradient(90deg, #2CC84B, #00BCD4, #3F51B5)',
                          }}
                        >
                          <p className="text-sm opacity-90 mb-2">Total Savings Over 25 Years</p>
                          <p className="text-4xl font-bold">
                            ₹{(parseInt(system.yearlySavings.replace(/[₹,]/g, '')) * 25).toLocaleString('en-IN')}
                          </p>
                        </div>

                        {/* CTA Button */}
                        <div className="mt-6 text-center">
                          <button className="ep-shimmer-btn px-8 py-3 rounded-lg font-semibold text-white text-lg">
                            Get Free Quote
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </div>

          {/* Note */}
          <p className="text-center text-sm text-gray-500 mt-8">
            * Calculations are approximate and based on average consumption patterns. Actual savings may vary based on location, usage, and sunlight availability.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
